import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Appointment } from "@myapp/contract";
import { FormSection } from "../../../../shared/ui/controls/FormSection/FormSection";
import { formatDateDDMMYYYY } from "../../../../shared/helpers/date";
import { getAppointmentStatusLabel, getAppointmentStatusColor } from "src/entities/appointment";
import { getTimeEntryCategoryLabel } from "src/entities/time-entry";
import { DraftAppointmentForm } from "src/features/disposition";
import { useUpdateAppointment, useTechnicianAppointments, appointmentApi } from "src/entities/appointment";
import { getErrorMessage } from "src/shared/lib/errors/getErrorMessage";
import { getDayRange } from "src/shared/lib/blockRanges";
import { parseAsString, useQueryState } from "nuqs";

interface Props {
  appointments: Appointment[];
}

const formatTime = (isoString: string): string => {
  const d = new Date(isoString);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const formatDuration = (minutes: number | null): string => minutes != null ? `${minutes}` : '—';

// Mirrors useDispositionDraftStore's buildFullDayAnfahrtStart / buildFullDayWorkDuration.
// Shift times are stored as "local time labelled UTC" — read with getUTC* and build a local Date.
const computeFullDay = (
  dayIso: string,
  shiftStart: string | null,
  shiftEnd: string | null,
): { plannedStart: string; workDuration: number } => {
  const [y, m, d] = dayIso.split('-').map(Number)
  const start = shiftStart
    ? (() => {
        const s = new Date(shiftStart)
        return new Date(y, m - 1, d, s.getUTCHours(), s.getUTCMinutes(), 0, 0).toISOString()
      })()
    : new Date(y, m - 1, d, 6, 0, 0, 0).toISOString()

  let workDuration = 900 // fallback: 960 min (6–22) − 60
  if (shiftStart && shiftEnd) {
    const s = new Date(shiftStart)
    const e = new Date(shiftEnd)
    const shiftMinutes =
      (e.getUTCHours() * 60 + e.getUTCMinutes()) -
      (s.getUTCHours() * 60 + s.getUTCMinutes())
    workDuration = Math.max(60, shiftMinutes - 60)
  }
  return { plannedStart: start, workDuration }
}

const BadgeCell: React.FC<{ color: string; label: string }> = ({ color, label }) => {
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-xs font-medium transition-all duration-150"
      style={{
        color,
        backgroundColor: color + "20",
        border: `1px solid ${color}40`,
      }}
    >
      {label}
    </span>
  );
};

export const AppointmentsSection: React.FC<Props> = ({ appointments }) => {
  const [appointmentId, setAppointmentId] = useQueryState(
    'appointmentId',
    parseAsString.withOptions({ history: 'replace', shallow: true, clearOnDefault: true })
  )
  const selectedAppointment = useMemo(
    () => appointments.find(a => a.id === appointmentId) ?? null,
    [appointments, appointmentId]
  )

  const [tradeCode, setTradeCode] = useState(0)
  const [description, setDescription] = useState<string | null>(null)
  const [files, setFiles] = useState<File[]>([])
  const [existingAttachments, setExistingAttachments] = useState<string[]>([])
  const [backendError, setBackendError] = useState<string | null>(null)
  const [plannedStart, setPlannedStart] = useState<string | null>(null)
  const [plannedTravel, setPlannedTravel] = useState<number>(0)
  const [plannedWork, setPlannedWork] = useState<number>(0)
  const [plannedDeparture, setPlannedDeparture] = useState<number>(0)
  const [isFullDay, setIsFullDay] = useState<boolean>(false)
  const [actualStart, setActualStart] = useState<string | null>(null)
  const [actualEnd, setActualEnd] = useState<string | null>(null)
  const [actualTravel, setActualTravel] = useState<number | null>(null)
  const [actualWork, setActualWork] = useState<number | null>(null)
  const [actualDeparture, setActualDeparture] = useState<number | null>(null)
  const { mutateAsync: updateAppointment, isPending } = useUpdateAppointment()

  const isSelectedCompleted =
    selectedAppointment?.status === 'COMPLETED' ||
    selectedAppointment?.status === 'COMPLETED_WITH_FOLLOW_UP'

  const shiftRange = useMemo(
    () => getDayRange(selectedAppointment?.plannedStart ?? new Date().toISOString()),
    [selectedAppointment?.plannedStart],
  )
  const { data: technicianData } = useTechnicianAppointments(shiftRange)
  const technicianShift = useMemo(() => {
    if (!selectedAppointment || !technicianData) return null
    return technicianData.technicians.find(
      t => t.technicianId === selectedAppointment.assignedTechnicianId,
    ) ?? null
  }, [technicianData, selectedAppointment])

  useEffect(() => {
    if (selectedAppointment) {
      setTradeCode(selectedAppointment.tradeCode)
      setDescription(selectedAppointment.description ?? null)
      setFiles([])
      setExistingAttachments(selectedAppointment.attachments ?? [])
      setBackendError(null)
      setPlannedStart(selectedAppointment.plannedStart)
      setPlannedTravel(selectedAppointment.plannedTravelDuration)
      setPlannedWork(selectedAppointment.plannedWorkDuration)
      setPlannedDeparture(selectedAppointment.plannedDepartureDuration)
      setIsFullDay(selectedAppointment.isFullDay ?? false)
      setActualStart(selectedAppointment.actualStart)
      setActualEnd(selectedAppointment.actualEnd)
      setActualTravel(selectedAppointment.actualTravelDuration)
      setActualWork(selectedAppointment.actualWorkDuration)
      setActualDeparture(selectedAppointment.actualDepartureDuration)
    }
  }, [selectedAppointment])

  const handleRemoveExistingAttachment = useCallback((index: number) => {
    setExistingAttachments((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleFullDayChange = useCallback((v: boolean) => {
    setIsFullDay(v)
    if (!v || !selectedAppointment) return
    const dayIso = (plannedStart ?? selectedAppointment.plannedStart).slice(0, 10)
    const { plannedStart: ps, workDuration } = computeFullDay(
      dayIso,
      technicianShift?.shiftStart ?? null,
      technicianShift?.shiftEnd ?? null,
    )
    setPlannedStart(ps)
    setPlannedTravel(30)
    setPlannedWork(workDuration)
    setPlannedDeparture(30)
  }, [plannedStart, selectedAppointment, technicianShift])

  const handleSave = async () => {
    if (!selectedAppointment) return

    try {
      setBackendError(null)
      let uploadedPaths: string[] = [];
      if (files.length > 0) {
        const { data } = await appointmentApi.uploadFiles(files);
        uploadedPaths = data;
      }
      const allAttachments = [...existingAttachments, ...uploadedPaths];

      const dto: Parameters<typeof updateAppointment>[0]['dto'] = {
        tradeCode,
        attachments: allAttachments,
        description,
      }

      if (plannedStart && plannedStart !== selectedAppointment.plannedStart) dto.plannedStart = plannedStart
      if (plannedTravel !== selectedAppointment.plannedTravelDuration) dto.plannedTravelDuration = plannedTravel
      if (plannedWork !== selectedAppointment.plannedWorkDuration) dto.plannedWorkDuration = plannedWork
      if (plannedDeparture !== selectedAppointment.plannedDepartureDuration) dto.plannedDepartureDuration = plannedDeparture
      if (isFullDay !== (selectedAppointment.isFullDay ?? false)) dto.isFullDay = isFullDay

      if (isSelectedCompleted) {
        if (actualStart && actualStart !== selectedAppointment.actualStart) dto.actualStart = actualStart
        if (actualEnd && actualEnd !== selectedAppointment.actualEnd) dto.actualEnd = actualEnd
        if (actualTravel != null && actualTravel !== selectedAppointment.actualTravelDuration) dto.actualTravelDuration = actualTravel
        if (actualWork != null && actualWork !== selectedAppointment.actualWorkDuration) dto.actualWorkDuration = actualWork
        if (actualDeparture != null && actualDeparture !== selectedAppointment.actualDepartureDuration) dto.actualDepartureDuration = actualDeparture
      }

      await updateAppointment({
        id: selectedAppointment.id,
        dto,
      })

      setAppointmentId(null)
    } catch (err) {
      setBackendError(getErrorMessage(err))
    }
  }

  const isShowActualDuration = (a: Appointment) => {
    return a.actualWorkDuration && (a.status == 'COMPLETED' || a.status == 'COMPLETED_WITH_FOLLOW_UP')
  }
  const isShowActualTime = (a: Appointment) => a.actualStart != null
  return (
    <>
      <FormSection title="Termine" cardClassName="p-0 overflow-hidden " className="w-full">
        {appointments.length === 0 ? (
          <p className="p-[30px_25px] text-gray-400 text-sm">Keine Termine vorhanden.</p>
        ) : (
          <table className="w-full text-sm font-['Roboto']">
            <thead>
              <tr className="border-b-2 border-gray-200 text-[#004494] text-left">
                <th className="px-3 max-[1535px]:px-2 py-3 pl-6 max-[1535px]:pl-4 font-medium">Termin</th>
                <th className="px-3 max-[1535px]:px-2 py-3 font-medium">Datum</th>
                <th className="px-3 max-[1535px]:px-2 py-3 font-medium">Uhrzeit</th>
                <th className="px-3 max-[1535px]:px-2 py-3 font-medium">Techniker</th>
                <th className="px-3 max-[1535px]:px-2 py-3 font-medium">Gewerk</th>
                <th className="px-3 max-[1535px]:px-2 py-3 font-medium">Soll-Dauer</th>
                {appointments.some(a => isShowActualDuration(a)) && <th className="px-3 max-[1535px]:px-2 py-3 font-medium">Ist-Dauer</th>}
                {appointments.some(a => isShowActualTime(a)) && <th className="px-3 max-[1535px]:px-2 py-3 font-medium">Beginn / Ende (ist)</th>}

                <th className="px-3 max-[1535px]:px-2 py-3 pr-6 max-[1535px]:pr-4 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {appointments.map((a) => (
                <tr
                  key={a.id}
                  className="group border-b border-gray-100 last:border-0 transition-colors duration-150 cursor-pointer"
                  style={{}}
                  onClick={() => setAppointmentId(a.id)}
                  onMouseEnter={e => (e.currentTarget.style.background = '#F0F6FF')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                >
                  <td className="px-3 max-[1535px]:px-2 py-4 pl-6 max-[1535px]:pl-4 text-gray-500 transition-colors duration-150">{a.sequenceNumber}.</td>
                  <td className="px-3 max-[1535px]:px-2 py-4 transition-colors duration-150">{formatDateDDMMYYYY(a.plannedStart)}</td>
                  <td className="px-3 max-[1535px]:px-2 py-4 transition-colors duration-150">{formatTime(a.plannedStart)}</td>
                  <td className="px-3 max-[1535px]:px-2 py-4 transition-colors duration-150 whitespace-normal break-words">{a.technician?.name ?? "—"}</td>
                  <td className="px-3 max-[1535px]:px-2 py-4 transition-colors duration-150 whitespace-normal break-words">{a.workOrder?.external?.gewerkeText ?? "—"}</td>
                  <td
                    className="px-3 max-[1535px]:px-2 py-4 transition-colors duration-150 whitespace-nowrap"
                    title={`Anfahrt: ${formatDuration(a.plannedTravelDuration)} Min.\nArbeit: ${formatDuration(a.plannedWorkDuration)} Min.\nAbfahrt: ${formatDuration(a.plannedDepartureDuration)} Min.`}
                  >
                    {`${formatDuration(a.plannedTravelDuration)} / ${formatDuration(a.plannedWorkDuration)} / ${formatDuration(a.plannedDepartureDuration)}`}
                  </td>
                  {isShowActualDuration(a) &&
                    <td
                      className="px-3 max-[1535px]:px-2 py-4 transition-colors duration-150 whitespace-nowrap"
                      title={`Anfahrt: ${formatDuration(a.actualTravelDuration)} Min.\nArbeit: ${formatDuration(a.actualWorkDuration)} Min.\nAbfahrt: ${formatDuration(a.actualDepartureDuration)} Min.`}
                    >
                      {`${formatDuration(a.actualTravelDuration)} / ${formatDuration(a.actualWorkDuration)} / ${formatDuration(a.actualDepartureDuration)}`}
                    </td>

                  }
                  {appointments.some(x => isShowActualTime(x)) && (
                    <td className="px-3 max-[1535px]:px-2 py-4 transition-colors duration-150 whitespace-nowrap">
                      {isShowActualTime(a)
                        ? `${a.actualStart ? formatTime(a.actualStart) : '—'} / ${a.actualEnd ? formatTime(a.actualEnd) : '—'}`
                        : '—'}
                    </td>
                  )}
                  <td className="px-3 max-[1535px]:px-2 py-4 pr-6 max-[1535px]:pr-4">
                    <div className="flex flex-col gap-1 items-start">
                      <BadgeCell color={getAppointmentStatusColor(a.status)} label={getAppointmentStatusLabel(a.status)} />

                      {a.needsRescheduling && (
                        <BadgeCell
                          color="#ef4444"
                          label={a.reschedulingReason
                            ? `Braucht Umdisponierung: ${getTimeEntryCategoryLabel(a.reschedulingReason)}`
                            : "Braucht Umdisponierung"
                          }
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </FormSection>
      {selectedAppointment && (
        <DraftAppointmentForm
          show={true}
          setShow={(show) => { if (!show) { setAppointmentId(null); setBackendError(null) } }}
          mode="edit"
          description={description}
          onDescriptionChange={setDescription}
          onDurationsChange={(d) => {
            setPlannedTravel(d.plannedTravelDuration)
            setPlannedWork(d.plannedWorkDuration)
            setPlannedDeparture(d.plannedDepartureDuration)
          }}
          onPlannedStartChange={setPlannedStart}
          isFullDay={isFullDay}
          onFullDayChange={handleFullDayChange}
          {...(isSelectedCompleted && {
            actualStart,
            actualEnd,
            actualDurations:
              actualTravel != null && actualWork != null
                ? {
                    actualTravelDuration: actualTravel,
                    actualWorkDuration: actualWork,
                    actualDepartureDuration: actualDeparture,
                  }
                : null,
            onActualStartChange: setActualStart,
            onActualEndChange: setActualEnd,
            onActualDurationsChange: (d: {
              actualTravelDuration: number
              actualWorkDuration: number
              actualDepartureDuration: number | null
            }) => {
              setActualTravel(d.actualTravelDuration)
              setActualWork(d.actualWorkDuration)
              setActualDeparture(d.actualDepartureDuration)
            },
          })}
          draft={{
            workOrderId: selectedAppointment.workOrderId,
            assignedTechnicianId: Number(selectedAppointment.assignedTechnicianId),
            plannedStart: plannedStart ?? selectedAppointment.plannedStart,
            tradeCode,
            plannedTravelDuration: plannedTravel,
            plannedWorkDuration: plannedWork,
            plannedDepartureDuration: plannedDeparture,
            attachments: existingAttachments,
          }}
          technicianName={selectedAppointment.technician?.name ?? null}
          onTradeCodeChange={setTradeCode}
          onSave={handleSave}
          isPending={isPending}
          backendError={backendError}
          files={files}
          onFilesChange={setFiles}
          existingAttachments={existingAttachments}
          onRemoveExistingAttachment={handleRemoveExistingAttachment}
        />
      )}
    </>
  );
};
